import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import UploadDocuments from "./UploadDocuments";
import InvestorNavbar from "./Navbar";

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://investor-backend-s4w5.onrender.com");

export default function InvestorDashboard() {
  const [data, setData] = useState([]);
  const [showDocs, setShowDocs] = useState(false);
  const [sendingWithdrawRequest, setSendingWithdrawRequest] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [form, setForm] = useState({
    depositAmount: "",
    tenure: 60,
    depositDate: new Date().toISOString().split("T")[0],
    withdrawAmount: "",
  });

  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!user) {
      window.location.href = "/login";
    }
  }, [user]);

  const loadData = async () => {
    try {
      if (!user?._id) return;

      const res = await axios.get(
        `${API_BASE_URL}/api/investments/user/${user._id}`,
      );

      setData(res.data || []);
    } catch (err) {
      console.error("Error loading investments:", err);
    }
  };

  useEffect(() => {
    if (user?._id) {
      loadData();
    }
  }, [user?._id]);

  const addDeposit = async () => {
    if (!form.depositAmount) {
      alert("Please enter deposit amount ❗");
      return;
    }

    const amount = Number(form.depositAmount);

    if (Number.isNaN(amount)) {
      alert("Please enter valid amount ❗");
      return;
    }

    if (amount < 100000) {
      alert("Minimum investment amount is ₹100000 ❗");
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/api/investments`, {
        userId: user?._id,
        depositAmount: amount,
      });

      alert(
        "Deposit Added ✅\nInvestment entry will be reflected after verification process.",
      );

      setForm({
        depositAmount: "",
        tenure: 60,
        depositDate: new Date().toISOString().split("T")[0],
        withdrawAmount: "",
      });

      loadData();
    } catch (err) {
      console.error("Error adding deposit:", err);
      console.log("Backend response:", err?.response?.data);

      alert(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Error adding deposit ❌",
      );
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN");
  };

  const formatDateTime = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-IN");
  };

  const formatCurrency = (amount) => {
    return `₹ ${Number(amount || 0).toLocaleString("en-IN")}`;
  };

  const getApprovedQuarterlyTotal = (quarterlyInterests = []) => {
    const now = new Date();

    return quarterlyInterests
      .filter(
        (q) =>
          q.adminApproved &&
          q.sentToCustomer &&
          q.visibleToInvestorAt &&
          new Date(q.visibleToInvestorAt) <= now,
      )
      .reduce((sum, q) => sum + Number(q.amount || 0), 0);
  };

  const visiblePaidRows = useMemo(() => {
    const now = new Date();

    return data.flatMap((item) =>
      (item.quarterlyInterests || [])
        .filter(
          (q) =>
            q.adminApproved &&
            q.sentToCustomer &&
            q.visibleToInvestorAt &&
            new Date(q.visibleToInvestorAt) <= now,
        )
        .sort((a, b) => new Date(b.paidDate) - new Date(a.paidDate))
        .map((q) => ({
          investmentId: item._id,
          interestId: q._id,
          customerName: item.customerName,
          quarterLabel: q.quarterLabel,
          amount: q.amount,
          paidDate: q.paidDate,
        })),
    );
  }, [data]);

  const hasVisiblePaidInterest = visiblePaidRows.length > 0;

  const getInvestmentStatus = (item) => {
    if (item?.isReleased) return "Deposit Released";
    if (item?.accountVerified) return "Verified";
    return "Pending Verify";
  };

  const getStatusStyle = (item) => {
    if (item?.isReleased) {
      return {
        color: "#2563eb",
        fontWeight: "bold",
      };
    }

    if (item?.accountVerified) {
      return {
        color: "green",
        fontWeight: "bold",
      };
    }

    return {
      color: "orange",
      fontWeight: "bold",
    };
  };

  const activeVerifiedInvestments = useMemo(() => {
    return data.filter((item) => item.accountVerified && !item.isReleased);
  }, [data]);

  const activeVerifiedTotal = activeVerifiedInvestments.reduce(
    (acc, item) => acc + Number(item.depositAmount || 0),
    0,
  );

  const totalInterestPaidTillDate = useMemo(() => {
    return activeVerifiedInvestments.reduce((sum, item) => {
      return sum + getApprovedQuarterlyTotal(item.quarterlyInterests || []);
    }, 0);
  }, [activeVerifiedInvestments]);

  const withdrawalAmount = Number(form.withdrawAmount || 0);
  const penaltyAmount =
    withdrawalAmount > 0 ? Number((withdrawalAmount * 0.15).toFixed(2)) : 0;
  const netAmountToRelease =
    withdrawalAmount > 0
      ? Number((withdrawalAmount - penaltyAmount).toFixed(2))
      : 0;

  const handlePrematureWithdraw = async () => {
    if (!form.withdrawAmount) {
      alert("Please enter withdrawal amount ❗");
      return;
    }

    const enteredWithdrawalAmount = Number(form.withdrawAmount);

    if (Number.isNaN(enteredWithdrawalAmount) || enteredWithdrawalAmount <= 0) {
      alert("Please enter valid withdrawal amount ❗");
      return;
    }

    if (enteredWithdrawalAmount > activeVerifiedTotal) {
      alert(
        "Withdrawal amount cannot be greater than total capital invested ❗",
      );
      return;
    }

    try {
      setSendingWithdrawRequest(true);

      await axios.post(`${API_BASE_URL}/api/withdraw-requests`, {
        userId: user?._id,
        customerName: user?.name || "N/A",
        requestDate: new Date(),
        totalCapitalInvested: activeVerifiedTotal,
        totalInterestPaidTillDate,
        withdrawalAmount: enteredWithdrawalAmount,
        penaltyPercent: 15,
        penaltyAmount,
        netAmountToRelease,
      });

      alert(
        "Withdrawal request sent successfully ✅\nMail has been sent to Accounts and Admin.",
      );

      setForm((prev) => ({
        ...prev,
        withdrawAmount: "",
      }));
    } catch (err) {
      console.error("Error sending withdrawal request:", err);
      console.log("Backend error response:", err?.response?.data);

      alert(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Error sending withdrawal request ❌",
      );
    } finally {
      setSendingWithdrawRequest(false);
    }
  };

  const withdrawTableRowStyle = {
    border: "1px solid #cbd5e1",
  };

  const withdrawLabelCellStyle = {
    ...withdrawTableRowStyle,
    width: isMobile ? "40%" : "36%",
    background: "#f8fafc",
    padding: isMobile ? "12px 10px" : "14px 16px",
    fontWeight: "700",
    color: "#0f172a",
    verticalAlign: "middle",
    fontSize: isMobile ? "13px" : "15px",
  };

  const withdrawValueCellStyle = {
    ...withdrawTableRowStyle,
    width: isMobile ? "60%" : "64%",
    background: "#fff",
    padding: isMobile ? "8px 10px" : "10px 12px",
    verticalAlign: "middle",
  };

  const readOnlyFieldStyle = {
    width: "100%",
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: isMobile ? "13px" : "15px",
    color: "#111827",
  };

  const editableFieldStyle = {
    width: "100%",
    padding: isMobile ? "10px" : "8px 10px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    outline: "none",
    fontSize: isMobile ? "14px" : "15px",
    color: "#111827",
    boxSizing: "border-box",
  };

  const thStyle = {
    background: "#24364a",
    color: "#fff",
    fontSize: isMobile ? "12px" : "14px",
    padding: isMobile ? "10px 8px" : "12px",
    whiteSpace: "nowrap",
  };

  const tdStyle = {
    fontSize: isMobile ? "12px" : "14px",
    padding: isMobile ? "10px 8px" : "10px",
    whiteSpace: "nowrap",
  };

  return (
    <>
      <InvestorNavbar />

      <div
        style={{
          padding: isMobile ? "12px" : "20px",
          maxWidth: "100%",
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >
        <h2
          style={{
            marginBottom: "16px",
            fontSize: isMobile ? "22px" : "28px",
            lineHeight: "1.3",
          }}
        >
          Lender Dashboard
        </h2>

        <button
          onClick={() => setShowDocs(!showDocs)}
          style={{
            marginBottom: "15px",
            padding: isMobile ? "10px 14px" : "8px 15px",
            background: "#007bff",
            color: "#fff",
            border: "none",
            cursor: "pointer",
            borderRadius: "8px",
            width: isMobile ? "100%" : "auto",
            fontSize: isMobile ? "14px" : "15px",
            fontWeight: "600",
          }}
        >
          {showDocs ? "Hide Documents" : "Upload Documents"}
        </button>

        {showDocs && (
          <UploadDocuments userId={user?._id} customerName={user?.name} />
        )}

        <div style={{ overflowX: "auto", width: "100%", marginTop: "15px" }}>
          <table
            border="1"
            width="100%"
            cellPadding="10"
            style={{
              borderCollapse: "collapse",
              minWidth: "1200px",
              background: "#fff",
            }}
          >
            <thead>
              <tr>
                <th style={thStyle}>Sr</th>
                <th style={thStyle}>Lender ID</th>
                <th style={thStyle}>Lender Name</th>
                <th style={thStyle}>Loan Amount</th>
                <th style={thStyle}>Interest Rate(annualy)</th>
                <th style={thStyle}>Tenure</th>
                <th style={thStyle}>Investment Date</th>
                <th style={thStyle}>Interest payout</th>
                <th style={thStyle}>Total Paid Interest</th>
                <th style={thStyle}>Maturity Date</th>
                <th style={thStyle}>Maturity Amount</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>

            <tbody>
              {data.length > 0 ? (
                data.map((item, index) => {
                  const isVerified = item.accountVerified;
                  const approvedQuarterlyTotal = getApprovedQuarterlyTotal(
                    item.quarterlyInterests,
                  );

                  return (
                    <tr
                      key={item._id}
                      style={{
                        background: item.isReleased ? "#eff6ff" : "#fff",
                      }}
                    >
                      <td style={tdStyle}>{index + 1}</td>
                      <td style={tdStyle}>{item.investorId || "-"}</td>
                      <td style={tdStyle}>
                        {item.customerName || user?.name || "-"}
                      </td>
                      <td style={tdStyle}>
                        {isVerified ? formatCurrency(item.depositAmount) : "-"}
                      </td>
                      <td style={tdStyle}>
                        {isVerified ? `${item.interestRate || 0}%` : "-"}
                      </td>
                      <td style={tdStyle}>
                        {item.tenure ? `${item.tenure / 12} Years` : "5 Years"}
                      </td>
                      <td style={tdStyle}>{formatDate(item.depositDate)}</td>
                      <td style={tdStyle}>
                        {isVerified ? item.interestType || "Quarterly" : "-"}
                      </td>
                      <td style={tdStyle}>
                        {isVerified
                          ? formatCurrency(approvedQuarterlyTotal)
                          : "-"}
                      </td>
                      <td style={tdStyle}>
                        {isVerified ? formatDate(item.maturityDate) : "-"}
                      </td>
                      <td style={tdStyle}>
                        {isVerified ? formatCurrency(item.depositAmount) : "-"}
                      </td>
                      <td style={{ ...tdStyle, ...getStatusStyle(item) }}>
                        {getInvestmentStatus(item)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="12"
                    style={{ textAlign: "center", padding: "14px" }}
                  >
                    No investment data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <h3
          style={{
            marginTop: "20px",
            fontSize: isMobile ? "18px" : "22px",
            lineHeight: "1.5",
          }}
        >
          Total Verified Loan Amount: {formatCurrency(activeVerifiedTotal)}
        </h3>

        <div style={{ marginTop: "30px" }}>
          <h3 style={{ fontSize: isMobile ? "18px" : "22px" }}>
            Paid Quarterly Interest
          </h3>

          <div style={{ overflowX: "auto", width: "100%" }}>
            <table
              border="1"
              width="100%"
              cellPadding="10"
              style={{
                borderCollapse: "collapse",
                minWidth: "700px",
                background: "#fff",
              }}
            >
              <thead>
                <tr>
                  <th style={thStyle}>Lender Name</th>
                  <th style={thStyle}>Quarter</th>
                  <th style={thStyle}>Interest Amount</th>
                  <th style={thStyle}>Paid Date</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>

              <tbody>
                {hasVisiblePaidInterest ? (
                  visiblePaidRows.map((row) => (
                    <tr key={`${row.investmentId}-${row.interestId}`}>
                      <td style={tdStyle}>
                        {row.customerName || user?.name || "-"}
                      </td>
                      <td style={tdStyle}>{row.quarterLabel || "-"}</td>
                      <td style={tdStyle}>{formatCurrency(row.amount)}</td>
                      <td style={tdStyle}>{formatDate(row.paidDate)}</td>
                      <td
                        style={{
                          ...tdStyle,
                          color: "green",
                          fontWeight: "bold",
                        }}
                      >
                        Paid
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="5"
                      style={{ textAlign: "center", padding: "14px" }}
                    >
                      No paid quarterly interest found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ marginTop: "30px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "20px",
              alignItems: "start",
            }}
          >
            <div
              style={{
                background: "#fff",
                padding: isMobile ? "16px" : "20px",
                borderRadius: "12px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                border: "1px solid #e5e7eb",
                minWidth: 0,
              }}
            >
              <h3
                style={{
                  marginBottom: "16px",
                  fontSize: isMobile ? "20px" : "22px",
                  fontWeight: "700",
                  color: "#111827",
                }}
              >
                Add Deposit
              </h3>

              <div style={{ display: "grid", gap: "12px" }}>
                <input
                  placeholder="Enter amount"
                  type="number"
                  value={form.depositAmount}
                  onChange={(e) => {
                    let value = e.target.value;

                    if (Number(value) > 1000000) {
                      alert("Max allowed is ₹10,00,000 ❗");
                      return;
                    }

                    setForm({ ...form, depositAmount: value });
                  }}
                  style={{
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: isMobile ? "16px" : "14px",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                />

                <input
                  value="9%"
                  readOnly
                  style={{
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: isMobile ? "16px" : "14px",
                    background: "#f9fafb",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                />

                <input
                  value="5 Years"
                  readOnly
                  style={{
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: isMobile ? "16px" : "14px",
                    background: "#f9fafb",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                />

                <input
                  type="date"
                  value={form.depositDate}
                  readOnly
                  style={{
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: isMobile ? "16px" : "14px",
                    background: "#f9fafb",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <button
                onClick={addDeposit}
                style={{
                  marginTop: "15px",
                  padding: "12px 16px",
                  background: "#16a34a",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "600",
                  width: isMobile ? "100%" : "auto",
                  fontSize: isMobile ? "15px" : "14px",
                }}
              >
                + Add Deposit
              </button>

              <p
                style={{
                  marginTop: "10px",
                  color: "#b45309",
                  fontWeight: "500",
                  fontSize: isMobile ? "14px" : "15px",
                }}
              >
                Minimum investment amount is ₹100000
              </p>
            </div>

            <div
              style={{
                background: "#fff",
                padding: isMobile ? "16px" : "20px",
                borderRadius: "12px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                border: "1px solid #e5e7eb",
                minWidth: 0,
              }}
            >
              <h3
                style={{
                  marginBottom: "16px",
                  fontSize: isMobile ? "20px" : "22px",
                  fontWeight: "700",
                  color: "#111827",
                }}
              >
                Withdrawal Request
              </h3>

              <div style={{ overflowX: "auto" }}>
                <table
                  width="100%"
                  cellSpacing="0"
                  cellPadding="0"
                  style={{
                    borderCollapse: "collapse",
                    border: "1px solid #cbd5e1",
                    borderRadius: "10px",
                    overflow: "hidden",
                    minWidth: isMobile ? "320px" : "100%",
                  }}
                >
                  <tbody>
                    <tr>
                      <td style={withdrawLabelCellStyle}>Customer Name</td>
                      <td style={withdrawValueCellStyle}>
                        <input
                          type="text"
                          readOnly
                          value={user?.name || "-"}
                          style={readOnlyFieldStyle}
                        />
                      </td>
                    </tr>

                    <tr>
                      <td style={withdrawLabelCellStyle}>Request Date</td>
                      <td style={withdrawValueCellStyle}>
                        <input
                          type="text"
                          readOnly
                          value={formatDateTime(new Date())}
                          style={readOnlyFieldStyle}
                        />
                      </td>
                    </tr>

                    <tr>
                      <td style={withdrawLabelCellStyle}>Withdrawal Amount</td>
                      <td style={withdrawValueCellStyle}>
                        <input
                          type="number"
                          placeholder="Enter withdrawal amount"
                          value={form.withdrawAmount || ""}
                          onChange={(e) =>
                            setForm({ ...form, withdrawAmount: e.target.value })
                          }
                          style={editableFieldStyle}
                        />
                      </td>
                    </tr>

                    <tr>
                      <td style={withdrawLabelCellStyle}>Penalty (15%)</td>
                      <td style={withdrawValueCellStyle}>
                        <input
                          type="text"
                          readOnly
                          value={formatCurrency(penaltyAmount)}
                          style={readOnlyFieldStyle}
                        />
                      </td>
                    </tr>

                    <tr>
                      <td style={withdrawLabelCellStyle}>
                        Net Amount to Release
                      </td>
                      <td style={withdrawValueCellStyle}>
                        <input
                          type="text"
                          readOnly
                          value={formatCurrency(netAmountToRelease)}
                          style={readOnlyFieldStyle}
                        />
                      </td>
                    </tr>

                    <tr>
                      <td style={withdrawLabelCellStyle}>
                        Total Interest Paid Till Date
                      </td>
                      <td style={withdrawValueCellStyle}>
                        <input
                          type="text"
                          readOnly
                          value={formatCurrency(totalInterestPaidTillDate)}
                          style={readOnlyFieldStyle}
                        />
                      </td>
                    </tr>

                    <tr>
                      <td style={withdrawLabelCellStyle}>
                        Total Capital Invested
                      </td>
                      <td style={withdrawValueCellStyle}>
                        <input
                          type="text"
                          readOnly
                          value={formatCurrency(activeVerifiedTotal)}
                          style={readOnlyFieldStyle}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <button
                onClick={handlePrematureWithdraw}
                disabled={sendingWithdrawRequest}
                style={{
                  marginTop: "18px",
                  padding: "12px 20px",
                  background: sendingWithdrawRequest ? "#9ca3af" : "#dc2626",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: sendingWithdrawRequest ? "not-allowed" : "pointer",
                  fontWeight: "700",
                  fontSize: "15px",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                {sendingWithdrawRequest
                  ? "Sending Request..."
                  : "Withdraw Request"}
              </button>
            </div>
          </div>

          <div
            style={{
              marginTop: "25px",
              width: "100%",
              padding: isMobile ? "14px" : "15px",
              background: "#fff7ed",
              border: "1px solid #fed7aa",
              borderRadius: "10px",
              color: "#9a3412",
              lineHeight: "1.6",
              boxSizing: "border-box",
              fontSize: isMobile ? "14px" : "15px",
            }}
          >
            <strong>Note:</strong>
            <p style={{ margin: "5px 0" }}>
              Investment entries will be reflected within <b>6 working days</b>.
            </p>
            <p style={{ margin: "5px 0" }}>
              Interest rate is fixed at <b>9%</b>.
            </p>
            <p style={{ margin: "5px 0" }}>
              Minimum investment amount is <b>₹100000</b>.
            </p>
            <p style={{ margin: "5px 0" }}>
              Quarterly interest will be visible only after it is sent to
              investor and 24 hours are completed.
            </p>
            <p style={{ margin: "5px 0" }}>
              Premature withdrawal will attract a <b>15% penalty</b>.
            </p>
            <p style={{ margin: "5px 0" }}>
              After the capital is released, the status will be shown as{" "}
              <b>Deposit Released</b>.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
